import {
  DEFAULT_CASHBOOK_DEBT_CATEGORIES,
  LEGACY_OPENING_DEBT_LINE_ID,
  listSelectableDebtCategories,
  sumOpeningDebtLines,
  type CashbookDebtCategory,
  type CashbookOpeningDebtLine
} from '@finvox/shared/cashbook';
import { parseMoneyInput, toMoney } from '@finvox/shared/money';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CashbookCategorySelect } from '@/components/cashbook/CashbookCategorySelect';
import { DebtOwnerSelect } from '@/components/cashbook/DebtOwnerSelect';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { MoneyInput } from '@/components/ui/MoneyInput';

type DraftLine = {
  id: string;
  creditor: string;
  amount: string;
  categoryKey: string;
  memo: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  lines: CashbookOpeningDebtLine[];
  debtOwners: string[];
  customDebtCategories?: CashbookDebtCategory[];
  onSave: (lines: CashbookOpeningDebtLine[]) => void;
};

function toDraft(lines: CashbookOpeningDebtLine[]): DraftLine[] {
  return lines.map((l) => ({
    id: l.id,
    creditor: l.creditor,
    amount: l.amount,
    categoryKey: l.categoryKey,
    memo: l.memo ?? ''
  }));
}

function newDraftId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `od-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function CashbookOpeningDebtsDialog({
  open,
  onClose,
  lines,
  debtOwners,
  customDebtCategories = [],
  onSave
}: Props) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<DraftLine[]>(() => toDraft(lines));
  const [creditor, setCreditor] = useState('');
  const [amount, setAmount] = useState('');
  const [categoryKey, setCategoryKey] = useState('supplier');
  const [memo, setMemo] = useState('');

  const categories = useMemo(
    () =>
      listSelectableDebtCategories(customDebtCategories).map((c) => ({
        value: c.key,
        label: DEFAULT_CASHBOOK_DEBT_CATEGORIES.some((d) => d.key === c.key)
          ? t(`cashbook.debts.categories.${c.labelKey}`)
          : c.key
      })),
    [customDebtCategories, t]
  );

  const owners = useMemo(() => {
    const set = new Set(debtOwners);
    for (const d of drafts) {
      const name = d.creditor.trim();
      if (name) set.add(name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [debtOwners, drafts]);

  const total = useMemo(() => {
    const parsed: CashbookOpeningDebtLine[] = drafts
      .filter((d) => toMoney(parseMoneyInput(d.amount)).gt(0))
      .map((d) => ({
        id: d.id,
        creditor: d.creditor.trim(),
        amount: parseMoneyInput(d.amount),
        categoryKey: d.categoryKey,
        ...(d.memo.trim() ? { memo: d.memo.trim() } : {})
      }));
    return sumOpeningDebtLines(parsed);
  }, [drafts]);

  if (!open) return null;

  function addLine() {
    const name = creditor.trim();
    const amt = parseMoneyInput(amount);
    if (!name) return;
    if (toMoney(amt).lte(0)) return;
    setDrafts((prev) => [
      ...prev,
      {
        id: newDraftId(),
        creditor: name,
        amount: amt,
        categoryKey,
        memo: memo.trim()
      }
    ]);
    setCreditor('');
    setAmount('');
    setMemo('');
    setCategoryKey('supplier');
  }

  function removeLine(id: string) {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
  }

  function handleSave() {
    const next: CashbookOpeningDebtLine[] = [];
    for (const d of drafts) {
      const amt = parseMoneyInput(d.amount);
      if (toMoney(amt).lte(0)) continue;
      const name = d.creditor.trim();
      const isLegacy = d.id === LEGACY_OPENING_DEBT_LINE_ID;
      if (!name && !isLegacy) continue;
      next.push({
        id: d.id,
        creditor: name,
        amount: amt,
        categoryKey: d.categoryKey,
        ...(d.memo.trim() ? { memo: d.memo.trim() } : {})
      });
    }
    onSave(next);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cashbook-opening-debts-title"
    >
      <Card className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden p-6 shadow-lg">
        <div className="shrink-0">
          <h2 id="cashbook-opening-debts-title" className="text-lg font-semibold">
            {t('cashbook.debts.openingLinesTitle')}
          </h2>
          <p className="mt-1 text-sm text-[var(--fv-text-muted)]">
            {t('cashbook.debts.openingLinesHint')}
          </p>
        </div>

        {drafts.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--fv-text-muted)]">
            {t('cashbook.debts.openingLinesEmpty')}
          </p>
        ) : (
          <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-lg border border-[var(--fv-border)]">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-[1] text-[var(--fv-text-muted)]">
                <tr>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.debts.creditor')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.category')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.memo')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-end font-medium">{t('cashbook.amount')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-end font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--fv-border)]">
                {drafts.map((row) => (
                  <tr key={row.id}>
                    <td className="px-3 py-2">
                      {row.creditor.trim()
                        ? row.creditor
                        : t('cashbook.debts.unassignedOpeningDebt')}
                    </td>
                    <td className="px-3 py-2">
                      {t(`cashbook.debts.categories.${row.categoryKey}`, {
                        defaultValue: row.categoryKey
                      })}
                    </td>
                    <td className="px-3 py-2 text-[var(--fv-text-muted)]">{row.memo || '—'}</td>
                    <td className="px-3 py-2 text-end tabular-nums">
                      <MoneyDisplay value={parseMoneyInput(row.amount)} />
                    </td>
                    <td className="px-3 py-2 text-end">
                      <Button variant="secondary" onClick={() => removeLine(row.id)}>
                        {t('common.remove')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 z-[1]">
                <tr className="border-t border-[var(--fv-border)] font-medium shadow-[0_-6px_12px_-8px_rgb(15_23_42_/_0.18)]">
                  <td className="bg-[var(--fv-bg)] px-3 py-2" colSpan={3}>
                    {t('cashbook.debts.totals')}
                  </td>
                  <td className="bg-[var(--fv-bg)] px-3 py-2 text-end tabular-nums">
                    <MoneyDisplay value={total} />
                  </td>
                  <td className="bg-[var(--fv-bg)] px-3 py-2" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div className="mt-4 shrink-0 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="w-full">
            <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.debts.creditor')}</label>
            <DebtOwnerSelect
              className="mt-1 w-full"
              value={creditor}
              onValueChange={setCreditor}
              owners={owners}
            />
          </div>
          <div className="w-full">
            <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.amount')}</label>
            <MoneyInput
              className="mt-1 w-full"
              allowEmpty
              value={amount}
              onValueChange={setAmount}
              aria-label={t('cashbook.amount')}
            />
          </div>
          <div className="w-full">
            <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.category')}</label>
            <CashbookCategorySelect
              className="mt-1 w-full"
              value={categoryKey}
              onValueChange={setCategoryKey}
              categories={categories}
              allowCreate
              aria-label={t('cashbook.category')}
            />
          </div>
          <div className="w-full">
            <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.memo')}</label>
            <Input
              className="mt-1 w-full"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              aria-label={t('cashbook.memo')}
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button
              variant="secondary"
              disabled={
                !creditor.trim() ||
                !categoryKey.trim() ||
                toMoney(parseMoneyInput(amount)).lte(0)
              }
              onClick={addLine}
            >
              {t('cashbook.debts.openingLinesAdd')}
            </Button>
          </div>
        </div>

        <div className="mt-6 flex shrink-0 justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              setDrafts(toDraft(lines));
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave}>{t('common.save')}</Button>
        </div>
      </Card>
    </div>
  );
}
